import Vue from 'vue'
import Router from 'vue-router'
import AuthLayout from '@/layout/AuthLayout'
Vue.use(Router)

// Per-route SEO metadata
const routeMeta = {
  'helper': {
    title: 'Quran Helper - Copy Arabic Glyphs, Fonts & Translations | Quran Clip Helper',
    description: 'Instantly copy Quran ayah glyphs, Uthmanic/Indopak fonts, and 100+ language translations for any verse. Free Quran content helper tool.',
    canonical: 'https://qurancliphelper.com/helper'
  },
  'video-creator': {
    title: 'Quran Video Creator - AI Ayah Detection & Video Clips | Quran Clip Helper',
    description: 'Create stunning Quran video clips with AI-powered ayah timing detection. Upload recitation audio, get precise timestamps, and generate video overlays with Arabic text and 100+ translations.',
    canonical: 'https://qurancliphelper.com/video-creator'
  },
  'audio-processing': {
    title: 'Quran Audio Processor - Enhance Recitation with Noise Reduction & EQ | Quran Clip Helper',
    description: 'Professional audio enhancement for Quran recitations. Apply noise reduction, equalizer, compression, reverb, and mastering. Presets for Mishary Rashid, Abdul Basit, Alaa Aqel. AI-recommended settings. Free.',
    canonical: 'https://qurancliphelper.com/audio-processing'
  },
  'privacy': {
    title: 'Privacy Policy | Quran Clip Helper',
    description: 'Privacy policy for Quran Clip Helper.',
    canonical: 'https://qurancliphelper.com/privacy'
  },
  'terms': {
    title: 'Terms of Service | Quran Clip Helper',
    description: 'Terms of service for Quran Clip Helper.',
    canonical: 'https://qurancliphelper.com/terms'
  }
}

const DEFAULT_TITLE = 'Quran Clip Helper - Free Quran Video & Audio Tools'
const DEFAULT_DESCRIPTION = 'Free online tools for Quran content creators. AI-powered video clip maker with ayah detection, plus professional audio processing for recitations.'
const DEFAULT_CANONICAL = 'https://qurancliphelper.com'

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setOgMeta(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

const router = new Router({
  mode: 'history',
  linkExactActiveClass: 'active',
  routes: [
    {
      path: '/',
      redirect: 'helper',
      component: AuthLayout,
      children: [
        {
          path: '/helper',
          name: 'helper',
          component: () => import(/* webpackChunkName: "demo" */ './views/Helper.vue')
        },
        {
          path: '/video-creator',
          name: 'video-creator',
          component: () => import(/* webpackChunkName: "video" */ './views/VideoCreator.vue')
        },
        {
          path: '/pageVisits',
          name: 'pageVisits',
          component: () => import(/* webpackChunkName: "demo" */ './views/Dashboard/PageVisitsTable.vue')
        },
        {
          path: '/audio-processing',
          name: 'audio-processing',
          component: () => import(/* webpackChunkName: "audio" */ './views/AudioProcessor.vue')
        },
        {
          path: '/admin',
          name: 'admin',
          component: () => import(/* webpackChunkName: "admin" */ './views/Admin.vue')
        },
        {
          path: '/privacy',
          name: 'privacy',
          component: () => import(/* webpackChunkName: "legal" */ './views/PrivacyPolicy.vue')
        },
        {
          path: '/terms',
          name: 'terms',
          component: () => import(/* webpackChunkName: "legal" */ './views/TermsOfService.vue')
        },
        {
          path: '/data-deletion',
          name: 'data-deletion',
          component: () => import(/* webpackChunkName: "legal" */ './views/DataDeletion.vue')
        }
      ]
    }
  ]
})

// After a new deploy, an open tab may try to lazy-load chunks whose
// hashed filenames no longer exist. Reload once to pick up the new build.
router.onError((error) => {
  if (/ChunkLoadError|Loading( CSS)? chunk .* failed/i.test(error && (error.name + ' ' + error.message))) {
    const key = 'chunk_reload_ts';
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last > 15000) {
      sessionStorage.setItem(key, String(Date.now()));
      window.location.reload();
    }
  }
})

// Per-page title, meta, canonical, and Analytics
router.afterEach((to) => {
  const meta = routeMeta[to.name] || {}
  const title = meta.title || DEFAULT_TITLE
  const description = meta.description || DEFAULT_DESCRIPTION
  const canonical = meta.canonical || DEFAULT_CANONICAL

  // Title
  document.title = title

  // Meta tags
  setMeta('description', description)
  setOgMeta('og:title', title)
  setOgMeta('og:description', description)
  setOgMeta('og:url', canonical)
  setMeta('twitter:title', title)
  setMeta('twitter:description', description)

  // Canonical
  setCanonical(canonical)

  // GA4
  if (typeof window.gtag === 'function') {
    window.gtag('config', 'G-NVB1KXZE2N', {
      page_path: to.fullPath,
      page_title: title
    })
  }
})

export default router
